/**
 * OpenUI Tool Provider
 *
 * Client-side function map for Query() / Mutation() — calls existing Losi
 * cookie-auth APIs so generative UI can refresh without an LLM round-trip.
 *
 * Primary responsibilities:
 * - Resolve space_id / workspace_id from args or chat context defaults
 * - Normalize API payloads into { rows, count, … } shapes for OpenUI bindings
 *
 * Dependencies:
 * - /api/spaces, /api/spaces/:id/tasks|notes, /api/crm/contacts
 */

'use client'

import { dispatchOpenUISpaceTasksChanged } from '@/lib/openui/space-tasks-events'

export type OpenUIToolContext = {
  spaceId?: string | null
  workspaceId?: string | null
}

type JsonRecord = Record<string, unknown>

async function fetchJson<T>(
  input: string,
  init?: RequestInit
): Promise<T> {
  const res = await fetch(input, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {})
    }
  })

  const text = await res.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = { error: text }
    }
  }

  if (!res.ok) {
    const message =
      typeof data === 'object' &&
      data &&
      'error' in data &&
      typeof (data as { error?: unknown }).error === 'string'
        ? (data as { error: string }).error
        : `Request failed (${res.status})`
    throw new Error(message)
  }

  return data as T
}

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as JsonRecord)
    : {}
}

function resolveSpaceId(args: JsonRecord, ctx: OpenUIToolContext) {
  const fromArgs = typeof args.space_id === 'string' ? args.space_id.trim() : ''
  const fromCtx = typeof ctx.spaceId === 'string' ? ctx.spaceId.trim() : ''
  const spaceId = fromArgs || fromCtx
  if (!spaceId) {
    throw new Error('No Space selected. Pass space_id or open a Space first.')
  }
  return spaceId
}

function resolveWorkspaceId(args: JsonRecord, ctx: OpenUIToolContext) {
  const fromArgs =
    typeof args.workspace_id === 'string' ? args.workspace_id.trim() : ''
  const fromCtx =
    typeof ctx.workspaceId === 'string' ? ctx.workspaceId.trim() : ''
  const workspaceId = fromArgs || fromCtx
  if (!workspaceId) {
    throw new Error(
      'No workspace selected. Pass workspace_id or select a workspace first.'
    )
  }
  return workspaceId
}

function normalizeTaskRow(task: JsonRecord) {
  const title =
    typeof task.title === 'string'
      ? task.title
      : typeof task.name === 'string'
        ? task.name
        : 'Untitled task'
  const status =
    typeof task.status === 'string'
      ? task.status
      : typeof task.state === 'string'
        ? task.state
        : 'todo'
  const dueRaw =
    task.due_date || task.due_at || task.deadline || task.due || null
  const due =
    typeof dueRaw === 'string'
      ? dueRaw.slice(0, 10)
      : dueRaw
        ? String(dueRaw).slice(0, 10)
        : ''
  const summary =
    typeof task.description === 'string'
      ? task.description.slice(0, 160)
      : typeof task.summary === 'string'
        ? task.summary.slice(0, 160)
        : ''

  return {
    id: typeof task.id === 'string' ? task.id : String(task.id || ''),
    title,
    status,
    due,
    summary
  }
}

function isDoneStatus(status: string) {
  const normalized = status.toLowerCase()
  return (
    normalized === 'done' ||
    normalized === 'completed' ||
    normalized === 'complete' ||
    normalized === 'closed'
  )
}

function isDueToday(due: string) {
  if (!due) return false
  const today = new Date().toISOString().slice(0, 10)
  return due.slice(0, 10) === today
}

async function listSpaceTasks(args: JsonRecord, ctx: OpenUIToolContext) {
  const spaceId = resolveSpaceId(args, ctx)
  const params = new URLSearchParams()
  if (typeof args.status === 'string' && args.status.trim()) {
    params.set('status', args.status.trim())
  }
  const qs = params.toString()
  const data = await fetchJson<unknown>(
    `/api/spaces/${encodeURIComponent(spaceId)}/tasks${qs ? `?${qs}` : ''}`
  )
  const list = Array.isArray(data)
    ? data
    : Array.isArray(asRecord(data).tasks)
      ? (asRecord(data).tasks as unknown[])
      : Array.isArray(asRecord(data).rows)
        ? (asRecord(data).rows as unknown[])
        : []

  const limit =
    typeof args.limit === 'number' && args.limit > 0
      ? Math.min(Math.floor(args.limit), 100)
      : 25

  const rows = list.map(item => normalizeTaskRow(asRecord(item))).slice(0, limit)
  const open_count = rows.filter(row => !isDoneStatus(row.status)).length
  const done_count = rows.filter(row => isDoneStatus(row.status)).length

  return {
    rows,
    count: rows.length,
    open_count,
    done_count
  }
}

async function createSpaceTask(args: JsonRecord, ctx: OpenUIToolContext) {
  const spaceId = resolveSpaceId(args, ctx)
  const title = typeof args.title === 'string' ? args.title.trim() : ''
  if (!title) throw new Error('title is required')

  const body: JsonRecord = {
    title,
    ...(typeof args.description === 'string'
      ? { description: args.description }
      : {}),
    ...(typeof args.status === 'string' ? { status: args.status } : {}),
    ...(typeof args.due_date === 'string' ? { due_date: args.due_date } : {})
  }

  const created = await fetchJson<JsonRecord>(
    `/api/spaces/${encodeURIComponent(spaceId)}/tasks`,
    { method: 'POST', body: JSON.stringify(body) }
  )

  dispatchOpenUISpaceTasksChanged({ spaceId, reason: 'create' })

  return {
    success: true,
    id: typeof created.id === 'string' ? created.id : String(created.id || ''),
    title:
      typeof created.title === 'string' ? created.title : title
  }
}

async function updateSpaceTask(args: JsonRecord, ctx: OpenUIToolContext) {
  const spaceId = resolveSpaceId(args, ctx)
  const taskId = typeof args.task_id === 'string' ? args.task_id.trim() : ''
  if (!taskId) throw new Error('task_id is required')

  const body: JsonRecord = {}
  if (typeof args.title === 'string') body.title = args.title
  if (typeof args.status === 'string') body.status = args.status
  if (Object.keys(body).length === 0) {
    throw new Error('Provide title and/or status to update')
  }

  const updated = await fetchJson<JsonRecord>(
    `/api/spaces/${encodeURIComponent(spaceId)}/tasks/${encodeURIComponent(taskId)}`,
    { method: 'PATCH', body: JSON.stringify(body) }
  )

  dispatchOpenUISpaceTasksChanged({ spaceId, reason: 'update' })

  return {
    success: true,
    id: typeof updated.id === 'string' ? updated.id : taskId
  }
}

async function listSpaceNotes(args: JsonRecord, ctx: OpenUIToolContext) {
  const spaceId = resolveSpaceId(args, ctx)
  const data = await fetchJson<unknown>(
    `/api/spaces/${encodeURIComponent(spaceId)}/notes`
  )
  const list = Array.isArray(data) ? data : []
  const limit =
    typeof args.limit === 'number' && args.limit > 0
      ? Math.min(Math.floor(args.limit), 100)
      : 20

  const rows = list.slice(0, limit).map(item => {
    const note = asRecord(item)
    return {
      id: typeof note.id === 'string' ? note.id : String(note.id || ''),
      title:
        typeof note.title === 'string'
          ? note.title
          : typeof note.name === 'string'
            ? note.name
            : 'Untitled note',
      updated_at:
        typeof note.updated_at === 'string'
          ? note.updated_at
          : typeof note.updatedAt === 'string'
            ? note.updatedAt
            : ''
    }
  })

  return { rows, count: rows.length }
}

async function listSpaces(args: JsonRecord, ctx: OpenUIToolContext) {
  const workspaceId = resolveWorkspaceId(args, ctx)
  const params = new URLSearchParams({ workspace_id: workspaceId })
  if (typeof args.q === 'string' && args.q.trim()) {
    params.set('q', args.q.trim())
  }
  const data = await fetchJson<unknown>(`/api/spaces?${params.toString()}`)
  const list = Array.isArray(data) ? data : []
  const limit =
    typeof args.limit === 'number' && args.limit > 0
      ? Math.min(Math.floor(args.limit), 100)
      : 40

  const rows = list.slice(0, limit).map(item => {
    const space = asRecord(item)
    return {
      id: typeof space.id === 'string' ? space.id : String(space.id || ''),
      name: typeof space.name === 'string' ? space.name : 'Untitled space',
      description:
        typeof space.description === 'string' ? space.description.slice(0, 160) : ''
    }
  })

  return { rows, count: rows.length }
}

async function listCrmContacts(args: JsonRecord, ctx: OpenUIToolContext) {
  const workspaceId = resolveWorkspaceId(args, ctx)
  const limit =
    typeof args.limit === 'number' && args.limit > 0
      ? Math.min(Math.floor(args.limit), 200)
      : 40
  const params = new URLSearchParams({
    workspace_id: workspaceId,
    limit: String(limit)
  })
  const data = await fetchJson<unknown>(`/api/crm/contacts?${params.toString()}`)
  const record = asRecord(data)
  const list = Array.isArray(data)
    ? data
    : Array.isArray(record.contacts)
      ? (record.contacts as unknown[])
      : Array.isArray(record.rows)
        ? (record.rows as unknown[])
        : []

  const q =
    typeof args.q === 'string' ? args.q.trim().toLowerCase() : ''

  const rows = list
    .map(item => {
      const contact = asRecord(item)
      const first =
        typeof contact.first_name === 'string' ? contact.first_name : ''
      const last =
        typeof contact.last_name === 'string' ? contact.last_name : ''
      const name =
        [first, last].filter(Boolean).join(' ').trim() ||
        (typeof contact.name === 'string' ? contact.name : 'Unknown contact')
      const email = typeof contact.email === 'string' ? contact.email : ''
      const company =
        typeof contact.company === 'string'
          ? contact.company
          : typeof asRecord(contact.company).name === 'string'
            ? String(asRecord(contact.company).name)
            : ''
      const status =
        typeof contact.status === 'string'
          ? contact.status
          : typeof contact.lifecycle_stage === 'string'
            ? contact.lifecycle_stage
            : ''
      return {
        id: typeof contact.id === 'string' ? contact.id : String(contact.id || ''),
        name,
        email,
        company,
        status
      }
    })
    .filter(row => {
      if (!q) return true
      return (
        row.name.toLowerCase().includes(q) ||
        row.email.toLowerCase().includes(q) ||
        row.company.toLowerCase().includes(q)
      )
    })
    .slice(0, limit)

  return { rows, count: rows.length }
}

async function getTaskMetrics(args: JsonRecord, ctx: OpenUIToolContext) {
  const listed = await listSpaceTasks({ ...args, limit: 100 }, ctx)
  const open = listed.rows.filter(row => !isDoneStatus(row.status)).length
  const done = listed.rows.filter(row => isDoneStatus(row.status)).length
  const due_today = listed.rows.filter(row => isDueToday(row.due)).length
  return {
    open,
    done,
    due_today,
    total: listed.rows.length
  }
}

export function createLosiOpenUIToolProvider(ctx: OpenUIToolContext = {}) {
  return {
    list_space_tasks: (args: JsonRecord = {}) => listSpaceTasks(args, ctx),
    create_space_task: (args: JsonRecord = {}) => createSpaceTask(args, ctx),
    update_space_task: (args: JsonRecord = {}) => updateSpaceTask(args, ctx),
    list_space_notes: (args: JsonRecord = {}) => listSpaceNotes(args, ctx),
    list_spaces: (args: JsonRecord = {}) => listSpaces(args, ctx),
    list_crm_contacts: (args: JsonRecord = {}) => listCrmContacts(args, ctx),
    get_task_metrics: (args: JsonRecord = {}) => getTaskMetrics(args, ctx)
  }
}

export type LosiOpenUIToolProvider = ReturnType<
  typeof createLosiOpenUIToolProvider
>
